import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Mail, Lock, Chrome, AlertCircle } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value.trim()
    });
    setError(''); // Clear error when user types
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          },
          redirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) {
        throw new Error('Error al iniciar sesión con Google');
      }
    } catch (err: any) {
      console.error('Error con Google Sign In:', err);
      setError('Error al iniciar sesión con Google. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('Por favor, completa todos los campos');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Por favor, introduce un email válido');
      return false;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setError('');
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Email o contraseña incorrectos');
        }
        throw error;
      }
      
      if (data.user) {
        window.location.href = '/dashboard';
      }
    } catch (err: any) {
      console.error('Error en el inicio de sesión:', err);
      setError(err.message || 'Error al iniciar sesión. Por favor, inténtalo de nuevo.');
      
      // Clear password field on error for security
      setFormData(prev => ({
        ...prev,
        password: ''
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-dark-card rounded-lg shadow-xl p-8">
        <h2 className="text-2xl font-bold text-white text-center mb-8">Iniciar Sesión</h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-600/20 border border-red-600 rounded flex items-center text-red-100 text-sm">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center space-x-2 bg-white text-gray-800 px-6 py-3 rounded hover:bg-gray-100 transition-all duration-300 mb-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Chrome className="h-5 w-5" />
          <span>Continuar con Google</span>
        </button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-dark-card text-gray-400">O inicia sesión con email</span>
          </div>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300">
              Correo Electrónico
            </label>
            <div className="mt-1 relative">
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                className="input-field pl-10"
                required
                autoComplete="email"
                disabled={loading}
              />
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                Contraseña
              </label>
              <Link 
                to="/reset-password"
                className="text-sm text-primary hover:text-primary-hover"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            <div className="mt-1 relative">
              <input
                type="password"
                id="password"
                value={formData.password}
                onChange={handleChange}
                className="input-field pl-10"
                required
                autoComplete="current-password"
                disabled={loading}
              />
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Iniciando sesión...
              </>
            ) : (
              'Iniciar Sesión'
            )}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-gray-300">
          ¿No tienes una cuenta?{' '}
          <Link to="/register" className="text-primary hover:text-primary-hover">
            Regístrate aquí
          </Link>
        </p>

        <p className="mt-4 text-center text-xs text-gray-400">
          Al iniciar sesión, aceptas nuestra{' '}
          <Link to="/privacy" className="text-primary hover:text-primary-hover">
            Política de Privacidad
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;